/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { OLAPTransformerResult, useParameter, useDictionary, Column } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, tooltipPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { dictionaryService } from "../../../dataFlow";
import { arrayByLength, uniq } from "../../../../../shared";
import { last, maxBy, minBy, orderBy } from "lodash";

const CATEGORIES_AXIS = "calendar.default";
const SERIES_AXIS = "commodities.default";
const [MONTH_FREQUENCY, WEEK_FREQUENCY, YEAR_FREQUENCY] = [4, 7, 1];

export function useHighchartTransformer(olapData: OLAPTransformerResult | null): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();

  const columns = useMemo(() => {
    return olapData?.columns?.reduce(
      (result, column) => {
        const calendarSelection = String(column.selection[CATEGORIES_AXIS]);
        const calendarId = Number(calendarSelection);
        const frequency = Number(calendarSelection[0]);
        const [isMonth, isWeek, isYear] = [MONTH_FREQUENCY, WEEK_FREQUENCY, YEAR_FREQUENCY].map(f => f === frequency);

        if (isYear) {
          result.years.push(column);
        }
        if (isMonth) {
          result.monthes.push(column);
          result.weeksFromMonth.set(calendarId, 0);
        }
        if (isWeek) {
          result.weeks.push(column);
          const monthId = Number(`4${calendarSelection.slice(1, 7)}01`);
          const countWeeks = result.weeksFromMonth.get(monthId) || 0;
          result.weeksFromMonth.set(monthId, countWeeks + 1);
        }
        return result;
      },
      {
        monthes: ([] as Column[]),
        years: ([] as Column[]),
        weeks: ([] as Column[]),
        weeksFromMonth: new Map<number, number>(),
      }
    );
  }, [olapData]);

  const lastColumn = last(columns?.weeks || []);

  const [displayCategories, dataCategories] = useMemo(
    () => {
      const monthesCategories = uniq(columns?.monthes.map(column => {
        const header = column.headers.find(e => e.id === `${CATEGORIES_AXIS}.name`);
        const colName = header?.name || '';
        return colName;
      }) || []);

      const dataColumns = columns ? [...columns.years, ...columns.weeks] : [];
      const sortedDataColumns = orderBy(dataColumns, col => {
        const key = String(col.selection[CATEGORIES_AXIS]);
        return key.slice(1, 5);
      });
      const dataCategoriesResult = uniq(sortedDataColumns.map(column => {
        const header = column.headers.find(e => e.id === `${CATEGORIES_AXIS}.name`);
        const colName = header?.name || '';
        return colName;
      }) || []);
      return [monthesCategories, dataCategoriesResult];
    },
    [columns],
  );

  const colors = ['#0F8AC0', '#1BB8C2', '#1CA955', '#E38114', '#DD5C3D', '#8F8F8F', '#D9D10C', '#2D5EDA', '#784FBA', '#D656A3'];

  const allPoints = olapData?.rows.flatMap(row => row.points.map(point => Math.round(point.value))) || [0];
  const tickPositions = olapData?.rows.map((row) => {
    const weekId = lastColumn?.selection[CATEGORIES_AXIS];
    const yearId = Number(`1${String(weekId).slice(1, 5)}0101`);
    const monthId = Number(`4${String(weekId).slice(1, 7)}01`);
    const isSingleWeek = columns?.weeksFromMonth.get(monthId) === 1;
    const lastPoint = row.points.find(point => point.column.selection[CATEGORIES_AXIS] === (isSingleWeek ? yearId : weekId));
    return Math.round(lastPoint?.value || 0);
  });
  let minYAxis = Math.floor((Math.min(...allPoints) - 50) / 100) * 100;
  if (minYAxis < 0) {
    minYAxis = 0;
  }
  const maxYAxis = Math.ceil((Math.max(...allPoints) + 50) / 100) * 100;
  const options = useChartOptions({
    tooltip: {
      outside: true,
      useHTML: true,
      formatter: function () {
        const commodity = this.point.series.name;
        const unit = 'USD/mt';
        const calendar = this.point.category;
        const pointValue = Highcharts.numberFormat(this.point.y || 0, 0);
        return `<div>
            <b>${commodity}</b><br/>
            <b>${calendar}</b>: <span>${pointValue} ${unit}</span>
        </div>`;
      },
    },
  }, [
    chartPipe("spline"),
    legendPipe("bottom", {
      labelFormatter: function () {
        const { name } = this;
        return name;
      }
    }),
    addXAxisPipe(dataCategories, {
      opposite: true,
      visible: false,
    }),
    addXAxisPipe(displayCategories, {
      labels: {
        rotation: -45,
      }
    }),
    addYAxisPipe({
      min: minYAxis,
      gridLineColor: '#fff',
      max: maxYAxis,
      labels: {
        enabled: true,
        formatter: function () {
          var label = this.axis.defaultLabelFormatter.call(this);
          if (/^[0-9]{4}$/.test(label)) {
            return Highcharts.numberFormat(this.value, 0);
          }
          return label;
        },
      },
    }),
    addYAxisPipe({
      opposite: true,
      min: minYAxis,
      max: maxYAxis,
      tickPositions: [minYAxis, ...(tickPositions || []), maxYAxis],
      gridLineColor: '#fff',
      labels: {
        enabled: true,
        useHTML: true,
        formatter: function () {
          const value = this.value;
          if (tickPositions?.includes(value)) {
            const tickColor = colors[tickPositions.indexOf(value)];
            return `<span style="color: ${tickColor}">${Highcharts.numberFormat(value, 0)}</span>`;
          }
          return '';
        },
      },
    }),
  ]);

  useEffect(() => {
    if (olapData) {
      const series: SeriesOptionsType[] = olapData.rows.map((row, rowIndex) => {
        const serieId = row.attributes[`${SERIES_AXIS}.id`];
        let serieName = row.attributes[`${SERIES_AXIS}.name`][9];
        const serieColor = colors[rowIndex];
        const dataColumns = columns ? [...columns.years, ...columns.weeks] : [];
        const sortedDataColumns = orderBy(dataColumns, col => {
          const key = String(col.selection[CATEGORIES_AXIS]);
          return key.slice(1, 5);
        });
        const serieData: (number | null)[] = sortedDataColumns.map(col => {
          const colId = col.selection[CATEGORIES_AXIS];
          const yearId = Number(`1${String(colId).slice(1, 5)}0101`);
          const monthId = Number(`4${String(colId).slice(1, 7)}01`);
          const isSingleWeek = columns?.weeksFromMonth.get(monthId) === 1;
          const colPoint = row.points.find(point =>
            point.column.selection[CATEGORIES_AXIS] === (isSingleWeek ? yearId : colId)
          );
          const isYear = Number(String(colId)[0]) === YEAR_FREQUENCY;
          const isHidden = isSingleWeek && !isYear
          const colData = isHidden ? null : colPoint?.value;
          return colData || null;
        });

        const isSingleData = serieData.filter(Boolean).length === 1;
        return {
          type: "spline",
          yAxis: 0,
          xAxis: 0,
          color: serieColor,
          dashStyle: "Solid",
          lineWidth: 3,
          zIndex: 1,
          marker: {
            radius: isSingleData ? 4 : 0,
          },
          dataLabels: {
            enabled: true,
            outside: true,
            allowOverlap: true,
            useHTML: true,
            formatter: function () {
              const value = this.y || 0;
              const displayValue = Math.round(value);
              if (!tickPositions?.includes(displayValue)) {

                const maxData = maxBy(this.series.data, point => point.y);
                const minData = minBy(this.series.data, point => point.y);
                const isVisible = [maxData, minData].some(p => p?.category === this.x);
                return isVisible ? `<span style="
                  color:${this['color']};
                  padding: 4px;
                  cursor: pointer;
                  border-radius: 50%;
                  background: rgb(255,255,255,.9);
                ">${displayValue}</span>` : '';
              }
              return '';
            }
          },
          showInLegend: true,
          states: {
            hover: {
              lineWidth: 0,
              lineWidthPlus: 0,
            },
          },
          name: serieName,
          id: serieId,
          data: isSingleData ? serieData.filter(Boolean) : serieData,
        }
      });

      const calendarSerieData: [string, number, number][] = columns?.monthes.map((colMonth, i) => {
        const monthId = colMonth.selection[CATEGORIES_AXIS] as number;
        const width = columns.weeksFromMonth.get(monthId) || 1;
        return [`${monthId}`, 0, columns?.monthes.length >= 88 ? 1 : width];
      }) || [];
      const calendarSerie: SeriesOptionsType = {
        type: 'variwide',
        id: 'calendar-serie',
        xAxis: 1,
        color: '#f6f6f6',
        showInLegend: false,
        zIndex: 0,
        dataLabels: {
          enabled: false,
        },
        data: calendarSerieData,
      };

      const result: Highcharts.Options = {
        ...options,
        series: [...series, calendarSerie],
      };

      setState(result);
    } else {
      setState(undefined);
    }
  }, [olapData, columns]);
  return state;
}
